/**
 * Support tickets: the vocabulary both sides share.
 *
 * NO ZOD HERE, same reason as every other file the barrel re-exports: the
 * request schemas are in `./contracts.ts` and stay on the server. This file is
 * the enum sets the database pins with triggers (migration 0013), the words a
 * screen prints for them, and the two limits a form checks before it asks.
 */

export const SUPPORT_TICKET_CATEGORIES = [
  'SUBMISSION_REVIEW',
  'REFERRAL',
  'PAYOUT',
  'CONNECTION',
  'ACCOUNT',
  'OTHER',
] as const;
export type SupportTicketCategory = (typeof SUPPORT_TICKET_CATEGORIES)[number];

export const SUPPORT_TICKET_CATEGORY_LABELS: Readonly<Record<SupportTicketCategory, string>> =
  Object.freeze({
    SUBMISSION_REVIEW: 'A submission decision',
    REFERRAL: 'The referral programme',
    PAYOUT: 'A payout',
    CONNECTION: 'A connected account',
    ACCOUNT: 'My account',
    OTHER: 'Something else',
  });

export const SUPPORT_TICKET_STATUSES = [
  'OPEN',
  'WAITING_ON_CREATOR',
  'RESOLVED',
  'CLOSED',
] as const;
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export const SUPPORT_TICKET_STATUS_LABELS: Readonly<Record<SupportTicketStatus, string>> =
  Object.freeze({
    OPEN: 'Open',
    WAITING_ON_CREATOR: 'Waiting on you',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
  });

/** The same four words from the operator's side of the desk. */
export const SUPPORT_TICKET_STATUS_LABELS_OPERATOR: Readonly<Record<SupportTicketStatus, string>> =
  Object.freeze({
    OPEN: 'Needs a reply',
    WAITING_ON_CREATOR: 'Waiting on the creator',
    RESOLVED: 'Resolved',
    CLOSED: 'Closed',
  });

/** A ticket somebody may still write on. */
export const SUPPORT_TICKET_LIVE_STATUSES = ['OPEN', 'WAITING_ON_CREATOR'] as const;

export function supportTicketIsLive(status: string): boolean {
  return (SUPPORT_TICKET_LIVE_STATUSES as readonly string[]).includes(status);
}

/**
 * What a ticket can point at. Each is a row the creator owns, and the API
 * answers 404 for a reference that is not theirs — never 403, which would say
 * "that id exists and belongs to somebody".
 */
export const SUPPORT_REF_TYPES = ['PostSubmission', 'Payout', 'ReferralBonus', 'SocialAccount'] as const;
export type SupportRefType = (typeof SUPPORT_REF_TYPES)[number];

export const SUPPORT_REF_TYPE_LABELS: Readonly<Record<SupportRefType, string>> = Object.freeze({
  PostSubmission: 'Submission',
  Payout: 'Payout',
  ReferralBonus: 'Referral bonus',
  SocialAccount: 'Connected account',
});

export const SUPPORT_MESSAGE_ROLES = ['CREATOR', 'OPERATOR', 'SYSTEM'] as const;
export type SupportMessageRole = (typeof SUPPORT_MESSAGE_ROLES)[number];

export const SUPPORT_SUBJECT_MAX = 120;
export const SUPPORT_BODY_MAX = 4000;
export const SUPPORT_SEARCH_MAX = 200;
export const SUPPORT_PAGE_SIZE = 25;

/** The category a reference implies, so a context pick fills the select. */
export function categoryForRef(refType: SupportRefType): SupportTicketCategory {
  switch (refType) {
    case 'PostSubmission':
      return 'SUBMISSION_REVIEW';
    case 'Payout':
      return 'PAYOUT';
    case 'ReferralBonus':
      return 'REFERRAL';
    case 'SocialAccount':
      return 'CONNECTION';
  }
}

/**
 * The subject a "Request a re-review" button prefills, and the body: the
 * rejection sentence, quoted, with room under it for the creator's own words.
 */
export function reReviewPrefill(input: {
  campaignName: string;
  platformLabel: string;
  rejectionReason: string | null;
}): { subject: string; body: string } {
  const subject = `Re-review request: ${input.campaignName} · ${input.platformLabel}`.slice(
    0,
    SUPPORT_SUBJECT_MAX,
  );
  const body = [
    'I would like this submission looked at again.',
    '',
    input.rejectionReason ? `It was rejected with: "${input.rejectionReason}"` : 'It was rejected.',
    '',
    'Why I think it should be reconsidered:',
    '',
  ].join('\n');
  return { subject, body };
}
